import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_TASK_OWNER_TYPES,
  HrLifecycleDashboard,
  HrOnboardingPlan,
  HrOnboardingTemplate,
  HrOnboardingTemplateTask
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-onboarding.component.html',
  styleUrls: ['./hr-onboarding.component.scss']
})
export class HrOnboardingComponent implements OnInit {
  activeListTab = 'templates';
  templateDialogVisible = false;
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  dashboard: HrLifecycleDashboard = {};
  templates: HrOnboardingTemplate[] = [];
  templateForm: HrOnboardingTemplate = this.emptyTemplate();
  taskDraft: HrOnboardingTemplateTask = this.emptyTask();

  readonly canManageOnboarding = this.permissionService.hasPermission(app_permissions.hr.onboarding.manage);
  categoryOptions: { label: string; value: string }[] = [];
  readonly ownerOptions = HR_TASK_OWNER_TYPES.map(value => ({ label: this.format(value), value }));
  readonly booleanOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  ngOnInit(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.categoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
    this.load();
  }

  get activePlans(): HrOnboardingPlan[] {
    return this.dashboard.activeOnboarding || [];
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Active Plans', value: this.dashboard.activeOnboardingPlans ?? 0, tone: 'info' },
      { label: 'Completed Plans', value: this.dashboard.completedOnboardingPlans ?? 0, tone: 'success' },
      { label: 'Overdue Plans', value: this.dashboard.overdueOnboardingPlans ?? 0, tone: (this.dashboard.overdueOnboardingPlans ?? 0) > 0 ? 'danger' : 'success' },
      { label: 'Overdue Tasks', value: this.dashboard.overdueOnboardingTasks ?? 0, tone: (this.dashboard.overdueOnboardingTasks ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Templates', value: this.templates.length },
      { label: 'Template Tasks', value: this.templates.reduce((sum, template) => sum + (template.tasks?.length || 0), 0) }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.lifecycleDashboard().subscribe({
      next: response => {
        this.dashboard = response.data || {};
        this.templates = this.dashboard.templates || [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to load onboarding workspace.' }),
      complete: () => this.loading = false
    });
  }

  editTemplate(template: HrOnboardingTemplate): void {
    this.templateForm = {
      ...template,
      tasks: (template.tasks || []).map(task => ({ ...task }))
    };
    this.taskDraft = this.emptyTask();
    this.templateDialogVisible = true;
  }

  addTask(): void {
    const tasks = this.templateForm.tasks || [];
    tasks.push({
      ...this.taskDraft,
      sequenceNo: this.taskDraft.sequenceNo || (tasks.length + 1) * 10,
      ownerType: this.taskDraft.ownerType || 'HR',
      requiredTask: this.taskDraft.requiredTask !== false,
      blocksActivation: this.taskDraft.blocksActivation !== false,
      active: true
    });
    this.templateForm.tasks = tasks;
    this.taskDraft = this.emptyTask((tasks.length + 1) * 10);
  }

  removeTask(index: number): void {
    this.templateForm.tasks = (this.templateForm.tasks || []).filter((_, rowIndex) => rowIndex !== index);
  }

  saveTemplate(): void {
    if (!this.canManageOnboarding) return;
    this.hrService.saveOnboardingTemplate(this.templateForm).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Onboarding', detail: 'Template saved.' });
        this.resetTemplate();
        this.templateDialogVisible = false;
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to save template.' })
    });
  }

  resetTemplate(): void {
    this.templateForm = this.emptyTemplate();
    this.taskDraft = this.emptyTask();
  }

  completedTasks(plan: HrOnboardingPlan): number {
    return (plan.tasks || []).filter(task => ['COMPLETED', 'WAIVED', 'NOT_REQUIRED', 'CANCELLED'].includes(task.taskStatus || '')).length;
  }

  planSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'COMPLETED') return 'success';
    if (status === 'IN_PROGRESS') return 'warn';
    if (status === 'OPEN') return 'info';
    if (status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private emptyTemplate(): HrOnboardingTemplate {
    return {
      templateCode: '',
      templateName: '',
      workerCategory: 'OFFICE',
      active: true,
      systemDefault: false,
      tasks: []
    };
  }

  private emptyTask(sequenceNo = 10): HrOnboardingTemplateTask {
    return {
      taskCode: '',
      taskName: '',
      ownerType: 'HR',
      sequenceNo,
      requiredTask: true,
      blocksActivation: true,
      dueOffsetDays: 3,
      active: true
    };
  }
}
